import { QuoteHeader, QuoteItem } from '@shared/schema';

export type LineDiscountType = 'percent' | 'absolute';

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function toSafeNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.replace(',', '.').trim();
    const parsed = parseFloat(normalized);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return fallback;
}

export function normalizeVatRate(rate: unknown): 0 | 7 | 19 {
  const numericRate = Math.round(toSafeNumber(rate, 19));
  if (numericRate === 0 || numericRate === 7 || numericRate === 19) {
    return numericRate;
  }
  return 19;
}

export function getVatRateFromHeader(header?: Partial<QuoteHeader> | null): 0 | 7 | 19 {
  return normalizeVatRate(header?.mwstSatz);
}

export function getLineBaseNet(item: Partial<QuoteItem>): number {
  const price = toSafeNumber((item as any).preis, 0);
  const quantity = toSafeNumber((item as any).menge, 0);
  return roundMoney(Math.max(0, price) * Math.max(0, quantity));
}

export function getLineDiscountAmount(item: Partial<QuoteItem>): number {
  const baseNet = getLineBaseNet(item);
  if (baseNet <= 0) {
    return 0;
  }

  const discountType: LineDiscountType = (item as any).rabattTyp === 'absolute' ? 'absolute' : 'percent';
  const discountValue = Math.max(0, toSafeNumber((item as any).rabattWert, 0));

  if (discountValue <= 0) {
    return 0;
  }

  if (discountType === 'absolute') {
    return roundMoney(Math.min(discountValue, baseNet));
  }

  const normalizedPercent = Math.min(100, discountValue);
  return roundMoney((baseNet * normalizedPercent) / 100);
}

export function getLineNetAfterDiscount(item: Partial<QuoteItem>): number {
  const baseNet = getLineBaseNet(item);
  const discount = getLineDiscountAmount(item);
  return roundMoney(Math.max(0, baseNet - discount));
}

export function getQuoteNetAfterLineDiscount(items: Array<Partial<QuoteItem>>): number {
  return roundMoney(items.reduce((sum, item) => sum + getLineNetAfterDiscount(item), 0));
}
